import { FC, useState } from 'react';
import { Delete, Visibility } from '@mui/icons-material';
import { numberToCurrencyString } from 'further-ui/utils';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import AlertDialog from 'components/AlertDialog';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import PageSection from 'components/PageSection';
import Spacing from 'components/Spacing';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import TableActions from 'components/Table/TableActions';

const pageId = 'unrealised-fx-history';

type Props = {
  vehicleId: string;
};

const UnrealisedFXGainOrLossSubTypeLabels = {
  FXGainPayable: 'FX Gain (Payable)',
  FXLossPayable: 'FX Loss (Payable)',
  FXGainReceivable: 'FX Gain (Receivable)',
  FXLossReceivable: 'FX Loss (Receivable)',
};

const PayablesOrReceivablesHistory: FC<Props> = ({ vehicleId }) => {
  const { vehicle } = useLayoutContext();
  const apiRef = useGridApiRef();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordIdToDelete, setRecordIdToDelete] = useState<string | null>(null);

  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'createdAt',
  });

  const { data, isLoading, refetch } =
    api.unrealisedFxGainOrLoss.listPosted.useQuery({
      vehicleId,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
    });

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteRecord, isPending: isDeleting } =
    api.unrealisedFxGainOrLoss.delete.useMutation(
      mutationOpts({
        successMessage: 'Unrealised FX gain/loss deleted successfully.',
        onSuccess: () => {
          refetch();
          setDeleteDialogOpen(false);
          setRecordIdToDelete(null);
        },
      }),
    );

  const handleOpenDeleteDialog = (recordId: string) => {
    setRecordIdToDelete(recordId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordIdToDelete(null);
  };

  const handleDelete = () => {
    if (recordIdToDelete) {
      deleteRecord({
        vehicleId,
        recordId: recordIdToDelete,
      });
    }
  };

  const columns: Array<Column> = [
    {
      field: 'ledgerDate',
      headerName: 'Ledger Date',
      renderCell: ({ row }) => row.ledgerDate.toLabel(),
      flex: 1,
    },
    {
      field: 'counterpartyLabel',
      headerName: 'Counterparty',
      renderCell: ({ row }) => row.counterpartyLabel ?? '-',
      flex: 1,
      sortable: false,
    },
    {
      field: 'gainOrLossSubType',
      headerName: 'Type',
      renderCell: ({ row }) =>
        UnrealisedFXGainOrLossSubTypeLabels[row.gainOrLossSubType],
      flex: 1,
      sortable: false,
    },
    {
      field: 'amountLcy',
      headerName: 'Amount (LCY)',
      renderCell: ({ row }) => {
        const isLoss =
          row.gainOrLossSubType === 'FXLossPayable' ||
          row.gainOrLossSubType === 'FXLossReceivable';
        const amount = isLoss ? -row.amountLcy : row.amountLcy;
        return numberToCurrencyString(amount, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: ({ row }) => (
        <TableActions
          stopPropagation
          actions={[
            {
              label: 'View',
              icon: Visibility,
              color: 'primary',
              link: `/unrealised-fx-history/${row.id}`,
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              disabled: !row.canDelete,
              tooltip: row.canDelete
                ? undefined
                : row.deleteReason ?? 'Cannot delete',
              onClick: () => handleOpenDeleteDialog(row.id!),
            },
          ]}
        />
      ),
      flex: 0.5,
      sortable: false,
    },
  ];

  if (!vehicle) {
    return null;
  }

  return (
    <Spacing>
      <PageSection
        title="History of posted unrealised FX gains/losses"
        withSpacing
      >
        <InteractiveTable
          apiRef={apiRef}
          id={pageId}
          columns={columns}
          rows={data?.results ?? []}
          pagination={pagination}
          loading={isLoading}
          scrollable
        />
        <AlertDialog
          onClose={handleCloseDeleteDialog}
          open={deleteDialogOpen}
          title="Delete unrealised FX gain/loss?"
          content="Are you sure you want to delete this unrealised FX gain/loss? This cannot be undone."
          btnLabels={{ confirm: 'Delete', cancel: 'Cancel' }}
          cancelBtnProps={{ variant: 'contained', color: 'primary' }}
          confirmBtnProps={{
            variant: 'outlined',
            color: 'error',
            disabled: isDeleting,
          }}
          onConfirm={handleDelete}
        />
      </PageSection>
    </Spacing>
  );
};

export default PayablesOrReceivablesHistory;
